// ═══════════════════════════════════════════════════════════════════════════
// Auth ErrorMap — Phase 2 Trust & Auth primitive.
//
// Centralises the translation from low-level Supabase / network error codes
// to user-facing messages WITH a recovery action. Without this layer, every
// auth form ends up calling `toast(error.message, 'error')`, surfacing
// strings like "Invalid login credentials" and "Failed to login. Please try
// again." that don't tell the user how to recover.
//
// Usage:
//   const mapped = mapAuthError(err);
//   toast(t(mapped.messageKey), 'error');
//   if (mapped.action === 'reset') navigate('/auth/reset-password');
//
// The mapping is data-only: a TypeScript object that React components can
// import without dragging i18n bootstrapping into this module. The actual
// translation happens at the call site.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Recovery action the UI should offer the user. A union — components do a
 * `switch` on this and render the right CTA (button / link / countdown).
 */
export type AuthErrorAction =
  | 'retry'        // generic "try again"
  | 'reset'        // "forgot password"
  | 'resend'       // "resend confirmation email"
  | 'signup'       // "create an account"
  | 'login'        // "log in" (e.g. account already exists)
  | 'wait'         // "rate limited — wait N minutes"
  | 'support'      // "contact support" (genuinely unrecoverable)
  | 'verify-email' // "you must verify your email first"
  | 'mfa';         // "additional verification required"

export interface MappedAuthError {
  /**
   * i18n key the component should pass to `t()` to render the message.
   * Always exists — there's a generic fallback for unknown errors.
   */
  messageKey: string;
  /**
   * Plain-text default that the component falls back to when the i18n key
   * is missing. Avoids users seeing a literal `auth.errors.something` if a
   * translation hasn't been added yet.
   */
  messageDefault: string;
  /** Optional hint key — sub-text rendered below the message. */
  hintKey?: string;
  hintDefault?: string;
  /** What CTA the UI should expose. */
  action: AuthErrorAction;
  /** Pre-filled link target for `action === 'reset' | 'signup' | 'login'`. */
  link?: string;
  /**
   * For `action === 'wait'`, how many seconds before the user can retry.
   * Components render a countdown and disable the form.
   */
  retryAfterSeconds?: number;
  /** Stable identifier for analytics events / Sentry tags. */
  code: string;
}

/**
 * Best-effort error normalisation. Supabase JS sometimes returns an Error,
 * sometimes a `{ message, status, name }` object, sometimes a `{ error_code,
 * msg }` payload. We accept anything and look for a recognisable shape.
 */
function getRawMessage(err: unknown): string {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object') {
    const candidate = err as { message?: unknown; error_description?: unknown; msg?: unknown };
    return String(candidate.message ?? candidate.error_description ?? candidate.msg ?? '');
  }
  return String(err);
}

function getStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object') {
    const s = (err as { status?: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return undefined;
}

/**
 * Match patterns are tried in order; the first hit wins. Each entry is
 * deliberately narrow — broad regexes invite false positives where a
 * "wrong-password" error gets flagged as "rate-limited" because the
 * fallback string from Supabase happens to mention "tries".
 */
const MATCHERS: Array<{
  test: (raw: string, status: number | undefined) => boolean;
  build: () => MappedAuthError;
}> = [
  // ── Network ─────────────────────────────────────────────────────────────
  {
    test: (raw, _status) =>
      /failed to fetch|networkerror|network request failed|fetch failed/i.test(raw),
    build: () => ({
      code: 'auth/network-error',
      messageKey: 'auth.errors.network',
      messageDefault: 'Connexion perdue. Vérifie ton réseau et réessaie.',
      action: 'retry',
    }),
  },

  // ── Rate limiting (status 429 or message-based) ─────────────────────────
  {
    test: (raw, status) =>
      status === 429 || /rate limit|too many requests|too many attempts/i.test(raw),
    build: () => ({
      code: 'auth/too-many-requests',
      messageKey: 'auth.errors.rateLimited',
      messageDefault: 'Trop de tentatives. Réessaie dans quelques minutes.',
      action: 'wait',
      retryAfterSeconds: 5 * 60,
    }),
  },

  // ── Email not confirmed yet ─────────────────────────────────────────────
  {
    test: (raw, _status) => /email not confirmed|email_not_confirmed|verify your email/i.test(raw),
    build: () => ({
      code: 'auth/email-not-verified',
      messageKey: 'auth.errors.emailNotVerified',
      messageDefault: 'Vérifie ton email avant de te connecter.',
      hintKey: 'auth.hints.checkInbox',
      hintDefault: 'Un lien de vérification a été envoyé à ta boîte de réception.',
      action: 'resend',
    }),
  },

  // ── Wrong credentials ───────────────────────────────────────────────────
  {
    test: (raw, _status) => /invalid login credentials|invalid credentials|wrong password|invalid_grant/i.test(raw),
    build: () => ({
      code: 'auth/wrong-password',
      messageKey: 'auth.errors.wrongCredentials',
      messageDefault: 'Email ou mot de passe incorrect.',
      hintKey: 'auth.hints.forgotPassword',
      hintDefault: 'Mot de passe oublié ?',
      action: 'reset',
      link: '/auth/reset-password',
    }),
  },

  // ── Account does not exist ──────────────────────────────────────────────
  {
    test: (raw, _status) => /user not found|no user with this email|user does not exist/i.test(raw),
    build: () => ({
      code: 'auth/user-not-found',
      messageKey: 'auth.errors.noAccount',
      messageDefault: "Aucun compte avec cet email.",
      hintKey: 'auth.hints.signUpInstead',
      hintDefault: 'Créer un compte ?',
      action: 'signup',
      link: '/auth/signup',
    }),
  },

  // ── Email already in use (signup path) ──────────────────────────────────
  {
    test: (raw, _status) =>
      /already registered|already exists|email already|user_already_exists/i.test(raw),
    build: () => ({
      code: 'auth/email-in-use',
      messageKey: 'auth.errors.emailInUse',
      messageDefault: 'Un compte existe déjà avec cet email.',
      hintKey: 'auth.hints.loginInstead',
      hintDefault: 'Se connecter ?',
      action: 'login',
      link: '/auth/login',
    }),
  },

  // ── Invalid email format ────────────────────────────────────────────────
  {
    test: (raw, _status) => /invalid email|email_address_invalid|email format/i.test(raw),
    build: () => ({
      code: 'auth/invalid-email',
      messageKey: 'auth.errors.invalidEmail',
      messageDefault: 'Format d\'email invalide.',
      action: 'retry',
    }),
  },

  // ── Weak password ───────────────────────────────────────────────────────
  {
    test: (raw, _status) =>
      /password.*(too short|weak|too weak|at least \d+)/i.test(raw) ||
      /weak_password/i.test(raw),
    build: () => ({
      code: 'auth/weak-password',
      messageKey: 'auth.errors.weakPassword',
      messageDefault: 'Mot de passe trop faible. Utilise au moins 8 caractères avec lettres, chiffres et symboles.',
      action: 'retry',
    }),
  },

  // ── MFA challenge required ──────────────────────────────────────────────
  {
    test: (raw, _status) => /mfa|two.?factor|otp/i.test(raw),
    build: () => ({
      code: 'auth/mfa-required',
      messageKey: 'auth.errors.mfaRequired',
      messageDefault: 'Vérification supplémentaire requise (2FA).',
      action: 'mfa',
    }),
  },

  // ── Session expired ─────────────────────────────────────────────────────
  {
    test: (raw, status) =>
      status === 401 || /session expired|jwt expired|invalid session/i.test(raw),
    build: () => ({
      code: 'auth/session-expired',
      messageKey: 'auth.errors.sessionExpired',
      messageDefault: 'Ta session a expiré. Reconnecte-toi.',
      action: 'login',
      link: '/auth/login',
    }),
  },
];

/**
 * Map any auth error shape into a MappedAuthError. The function never
 * throws and never returns null — there's a generic fallback for unknown
 * errors so the UI always has something to render.
 */
export function mapAuthError(err: unknown): MappedAuthError {
  const raw = getRawMessage(err);
  const status = getStatus(err);

  for (const m of MATCHERS) {
    if (m.test(raw, status)) {
      return m.build();
    }
  }

  // Generic fallback — preserves the underlying message for support context
  // but suggests a retry action. The `code` is stable so analytics can
  // count "unknown auth errors" trending up.
  return {
    code: 'auth/unknown',
    messageKey: 'auth.errors.unknown',
    messageDefault: raw || 'Une erreur est survenue. Réessaie.',
    action: 'retry',
  };
}

/**
 * Convenience helper: returns the message ready-to-toast. When i18n is
 * available at the call site, prefer `t(mapped.messageKey, mapped.messageDefault)`
 * over this function so users see the localised string.
 */
export function authErrorMessage(err: unknown): string {
  return mapAuthError(err).messageDefault;
}
